export const TEXT_LAYOUT_MODES = {
  ABSOLUTE: 'absolute',
  FRAME: 'frame',
};

export const DEFAULT_TEXT_STYLE = {
  font_key: '',
  font_size: null,
  font_style: 'regular',
  text_align: 'center',
  layout_mode: TEXT_LAYOUT_MODES.ABSOLUTE,
  x: null,
  y: null,
  frame_margin_top: 48,
  frame_margin_right: 48,
  frame_margin_bottom: 48,
  frame_margin_left: 48,
  vertical_align: 'center',
  horizontal_align: 'center',
  frame_width_percent: 100,
  auto_fit: true,
  min_font_size: 24,
};

const FONT_STYLES = ['regular', 'italic', 'bold', 'bold_italic'];
const TEXT_ALIGNS = ['left', 'center', 'right', 'start', 'end'];
const VERTICAL_ALIGNS = ['top', 'center', 'bottom'];
const HORIZONTAL_ALIGNS = ['start', 'center', 'end'];

export const toOptionalNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const clampNumber = (value, fallback, min, max) => {
  const number = toOptionalNumber(value);
  if (number === null) return fallback;
  return Math.min(max, Math.max(min, number));
};

export const normalizeTextStyle = (style, options = {}) => {
  const source = style && typeof style === 'object' ? style : {};
  const parsedFontSize = toOptionalNumber(source.font_size);
  const normalized = {
    ...DEFAULT_TEXT_STYLE,
    font_key: String(source.font_key || '').trim(),
    font_size: parsedFontSize === null ? null : Math.max(1, parsedFontSize),
    font_style: FONT_STYLES.includes(source.font_style) ? source.font_style : 'regular',
    text_align: TEXT_ALIGNS.includes(source.text_align || source.textAlign)
      ? (source.text_align || source.textAlign)
      : 'center',
    layout_mode: source.layout_mode === TEXT_LAYOUT_MODES.FRAME
      ? TEXT_LAYOUT_MODES.FRAME
      : TEXT_LAYOUT_MODES.ABSOLUTE,
    x: toOptionalNumber(source.x),
    y: toOptionalNumber(source.y),
    frame_margin_top: clampNumber(
      source.frame_margin_top ?? source.margin_top,
      DEFAULT_TEXT_STYLE.frame_margin_top,
      0,
      1112
    ),
    frame_margin_right: clampNumber(
      source.frame_margin_right ?? source.margin_right,
      DEFAULT_TEXT_STYLE.frame_margin_right,
      0,
      1144
    ),
    frame_margin_bottom: clampNumber(
      source.frame_margin_bottom ?? source.margin_bottom,
      DEFAULT_TEXT_STYLE.frame_margin_bottom,
      0,
      1112
    ),
    frame_margin_left: clampNumber(
      source.frame_margin_left ?? source.margin_left,
      DEFAULT_TEXT_STYLE.frame_margin_left,
      0,
      1144
    ),
    vertical_align: VERTICAL_ALIGNS.includes(source.vertical_align)
      ? source.vertical_align
      : DEFAULT_TEXT_STYLE.vertical_align,
    horizontal_align: HORIZONTAL_ALIGNS.includes(source.horizontal_align)
      ? source.horizontal_align
      : DEFAULT_TEXT_STYLE.horizontal_align,
    frame_width_percent: clampNumber(
      source.frame_width_percent,
      DEFAULT_TEXT_STYLE.frame_width_percent,
      10,
      100
    ),
    auto_fit: source.auto_fit !== false,
    min_font_size: clampNumber(source.min_font_size, DEFAULT_TEXT_STYLE.min_font_size, 1, 240),
  };

  if (options.includeUseDefault || Object.prototype.hasOwnProperty.call(source, 'use_default')) {
    normalized.use_default = source.use_default !== false;
  }

  if (normalized.frame_margin_top + normalized.frame_margin_bottom > 1112) {
    normalized.frame_margin_bottom = Math.max(0, 1112 - normalized.frame_margin_top);
  }
  if (normalized.frame_margin_left + normalized.frame_margin_right > 1144) {
    normalized.frame_margin_right = Math.max(0, 1144 - normalized.frame_margin_left);
  }
  if (normalized.font_size !== null) {
    normalized.min_font_size = Math.min(normalized.min_font_size, normalized.font_size);
  }

  return normalized;
};

export const buildTextStylePayload = (style, options = {}) => {
  const normalized = normalizeTextStyle(style);
  const payload = {
    font_key: normalized.font_key,
    font_size: normalized.font_size,
    font_style: normalized.font_style,
    text_align: normalized.text_align,
    layout_mode: normalized.layout_mode,
    x: normalized.layout_mode === TEXT_LAYOUT_MODES.ABSOLUTE ? normalized.x : null,
    y: normalized.layout_mode === TEXT_LAYOUT_MODES.ABSOLUTE ? normalized.y : null,
  };

  if (normalized.layout_mode === TEXT_LAYOUT_MODES.FRAME) {
    Object.assign(payload, {
      frame_margin_top: normalized.frame_margin_top,
      frame_margin_right: normalized.frame_margin_right,
      frame_margin_bottom: normalized.frame_margin_bottom,
      frame_margin_left: normalized.frame_margin_left,
      vertical_align: normalized.vertical_align,
      horizontal_align: normalized.horizontal_align,
      frame_width_percent: normalized.frame_width_percent,
      auto_fit: normalized.auto_fit,
      min_font_size: normalized.min_font_size,
    });
  }

  if (options.useDefault !== undefined) {
    payload.use_default = options.useDefault;
  }

  return payload;
};

export const resolveEffectiveTextStyle = (storyDefaultStyle, sceneOverride) => {
  if (sceneOverride && sceneOverride.use_default === false) {
    return normalizeTextStyle(sceneOverride);
  }
  return storyDefaultStyle ? normalizeTextStyle(storyDefaultStyle) : null;
};

export const replaceKidNameToken = (text, kidName) => String(text || '')
  .replace(/\{\{Kid_Name\}\}/g, String(kidName || 'Kid Name'));
